import React from "react";
import {
  Paper,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
  Stack,
  Box,
} from "@mui/material";
import { Link } from "react-router-dom";
import EventChip from "../EventChip";
import dayjs from "dayjs";
import { EventPartialDto } from "../../../types/eventTypes";
import EventRegistrationButton from "../EventRegistrationButton";
import { checkIfEventRegistrationIsOpen } from "../../../utils/EventsUtils";

interface EventsOverviewTableProps {
  events: EventPartialDto[];
  checkIfUserIsSignedUpForEvent: (event: EventPartialDto) => boolean;
  onSignUp: (event: EventPartialDto) => Promise<void>;
  onSignOut: (event: EventPartialDto) => Promise<void>;
}

/**
 * Component that displays the events in a table for desktop view, uses the EventsSignUpButton component to display the sign up button for each event
 * @param events The events to display
 * @param displayMonths Whether to display the months of the events or not
 * @param checkIfUserIsSignedUpForEvent The function to check if the user is signed up for an event, passed down to the EventRegistrationButton component to display the correct button for each event
 * @param onSignUp The function to sign up for an event, passed down to the EventRegistrationButton component to display the correct button for each event
 * @param onSignOut The function to sign out from an event, passed down to the EventRegistrationButton component to display the correct button for each event
 * @returns A table with the events, sorted by date, and a sign up button for each event if the user is not signed up for it yet and the registration deadline is not in the past
 */
const EventsOverviewTable = ({
  events,
  checkIfUserIsSignedUpForEvent,
  onSignUp,
  onSignOut,
}: EventsOverviewTableProps) => {
  return (
    <TableContainer component={Paper}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell></TableCell>
            <TableCell sx={{ fontWeight: "bold" }}>Event</TableCell>
            <TableCell sx={{ fontWeight: "bold" }}>Datum</TableCell>
            <TableCell sx={{ fontWeight: "bold" }}>Uhrzeit</TableCell>
            <TableCell sx={{ fontWeight: "bold" }}>Anmeldungsfrist</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {/** Show Months of events? Or just display all events?*/}
          {events.map((event, index) => (
            <React.Fragment key={event.eventId}>
              {index === 0 || dayjs(event.eventBeginn).month() !== dayjs(events[index - 1].eventBeginn).month() ? (
                <TableRow>
                  <TableCell colSpan={6} sx={{ p: 0, bgcolor: "#f6891f" }}>
                    <Typography color={"white"} fontWeight={"bold"} sx={{ ml: 2 }}>
                      {dayjs(event.eventBeginn).locale("de").format("MMMM YYYY")}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : null}
              <TableRow>
                <TableCell>
                  <Link
                    to={
                      event.type === "Workshop" ||
                      event.type === "Pflichtworkshop" ||
                      event.type === "Externer Workshop"
                        ? `/workshops/${event.workshopId}/${event.eventId}`
                        : `/veranstaltungen/${event.eventId}`
                    }
                  >
                    <EventChip type={event.type} size="small" />
                  </Link>
                </TableCell>
                <TableCell>
                  <Link
                    style={{ textDecoration: "none", color: "black" }}
                    to={
                      event.type === "Workshop" ||
                      event.type === "Pflichtworkshop" ||
                      event.type === "Externer Workshop"
                        ? `/workshops/${event.workshopId}/${event.eventId}`
                        : `/veranstaltungen/${event.eventId}`
                    }
                  >
                    {event.eventName}
                  </Link>
                </TableCell>
                <TableCell>
                  {dayjs(event.eventBeginn).format("DD.MM.YYYY")}
                  {dayjs(event.eventEnd) > dayjs(event.eventBeginn)
                    ? " - " + dayjs(event.eventEnd).format("DD.MM.YYYY")
                    : null}
                </TableCell>
                <TableCell>
                  {event.startTime ? event.startTime : null}
                  {event.startTime && !event.endTime ? null : " - "}
                  {event.endTime ? event.endTime : null}
                </TableCell>
                <TableCell>
                  <Stack justifyContent={"space-between"} direction={"row"} alignItems={"center"}>
                    {event.registrationTo && event.registrationFrom ? (
                      // check if registration start is in the future
                      dayjs(event.registrationFrom) > dayjs() ? (
                        // then display the registration start
                        <Typography variant={"subtitle2"} color="text.secondary">
                          {dayjs(event.registrationFrom).format("DD.MM.YYYY [/] HH:mm")}
                        </Typography>
                      ) : dayjs(event.registrationTo) > dayjs() ? (
                        // else check if registration deadline is in the future and display it
                        <Typography variant={"subtitle2"}>
                          {dayjs(event.registrationTo).format("DD.MM.YYYY [/] HH:mm")}
                        </Typography>
                      ) : (
                        <Typography variant={"caption"}>Abgelaufen</Typography>
                      )
                    ) : event.registrationTo ? (
                      // if the registrationStart is null check if the registrationDeadline is not null if so display it
                      dayjs(event.registrationTo) > dayjs() ? (
                        <Typography variant={"subtitle2"}>
                          {dayjs(event.registrationTo).format("DD.MM.YYYY [/] HH:mm")}
                        </Typography>
                      ) : (
                        <Typography variant={"caption"}>Abgelaufen</Typography>
                      )
                    ) : event.registrationFrom ? (
                      <Typography variant={"subtitle2"}>
                        {dayjs(event.registrationFrom).format("DD.MM.YYYY [/] HH:mm")}
                      </Typography>
                    ) : (
                      <Box />
                    )}
                    <Box ml={0.5}>
                      {dayjs(event.eventEnd) > dayjs() ? (
                        <EventRegistrationButton
                          isUserSignedUp={checkIfUserIsSignedUpForEvent(event)}
                          isRegistrationOpen={checkIfEventRegistrationIsOpen(event)}
                          isRegistrationFull={
                            event.maximumParticipants && event.amountOfParticipants
                              ? event.amountOfParticipants >= event.maximumParticipants
                              : false
                          }
                          onSignUp={async () => await onSignUp(event)}
                          onSignOut={async () => await onSignOut(event)}
                          size={"small"}
                        />
                      ) : null}
                    </Box>
                  </Stack>
                </TableCell>
              </TableRow>
            </React.Fragment>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default EventsOverviewTable;
